import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

export interface CameraCaptureHandle {
  getScreenshot: () => string | null;
}

export interface CameraCaptureProps {
  facingMode?: "user" | "environment";
  mirrored?: boolean;
  quality?: number;
  width?: number;
  height?: number;
  onError?: (message: string) => void;
  style?: React.CSSProperties;
  className?: string;
}

const CameraCapture = forwardRef<CameraCaptureHandle, CameraCaptureProps>(
  ({ facingMode = "user", mirrored = false, quality = 0.92, width = 1280, height = 720, onError, style, className }, ref) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
      let cancelled = false;

      const start = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              facingMode: { ideal: facingMode },
              width: { ideal: width },
              height: { ideal: height },
            },
          });
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
              videoRef.current?.play().catch(() => undefined);
              setReady(true);
            };
          }
        } catch (err) {
          const message =
            err instanceof DOMException ? err.message : "Camera access denied or unavailable.";
          onError?.(message);
        }
      };

      void start();

      return () => {
        cancelled = true;
        const stream = streamRef.current;
        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };
    }, [facingMode, width, height, onError]);

    useImperativeHandle(ref, () => ({
      getScreenshot: () => {
        const video = videoRef.current;
        if (!video || !ready) return null;
        const canvas = document.createElement("canvas");
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return null;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        if (mirrored) {
          ctx.translate(w, 0);
          ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0, w, h);
        return canvas.toDataURL("image/jpeg", quality);
      },
    }));

    return (
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={className}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: mirrored ? "scaleX(-1)" : undefined,
          ...style,
        }}
      />
    );
  }
);

CameraCapture.displayName = "CameraCapture";

export default CameraCapture;
